<template>
  <v-app class="bgImage">
    <navbar>
      <v-toolbar-title class="text-uppercase secondary--text text--darken-3">
        <span class="font-weight-light"
          >Download {{ trendReadings ? "" : "All" }}</span
        >
        <span class="font-weight-medium">{{ device.device_name }}</span>
        <span class="font-weight-light"> Trend Readings</span>
      </v-toolbar-title>
      <v-spacer></v-spacer>
    </navbar>
    <v-content class="secondary--text text--darken-2">
      <v-container fluid>
        <v-card>
          <m-errorbox ref="errorBox"></m-errorbox>
          <v-card-text>
            <v-row
              v-for="(deviceDetails, idx) of deviceDetailsFormattedList"
              :key="idx"
            >
              <v-col cols="2">
                <label>{{ deviceDetails.title }}:</label>
              </v-col>
              <v-col>
                <label class="font-weight-medium text--darken-1">{{
                  deviceDetails.value || "N/A"
                }}</label>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-text>
            <h3 class="darken-1 font-weight-medium">
              Download Device Readings by Date Range
            </h3>
            <p>
              Please enter a date range between
              {{ availableYears.previousYear }} and
              {{ availableYears.currentYear }}
            </p>
            <m-form
              v-slot="{ model }"
              :query="apiEndpoints.downloadAllReadings"
              :modelSrc="filterData"
              :rules="{}"
              :params="[$route.params.deviceId]"
              @http-response-success="downloadReadingsSuccess"
            >
              <v-row align="center">
                <v-col cols="2">
                  <m-textinput
                    name="start_date"
                    type="date"
                    label="From"
                    :model="model"
                  ></m-textinput>
                </v-col>
                <v-col cols="2">
                  <m-textinput
                    name="end_date"
                    type="date"
                    label="To"
                    :model="model"
                  ></m-textinput>
                </v-col>
                <v-col cols="2">
                  <v-row v-if="model.status.disabled" align="center">
                    <v-col cols="auto">
                      <v-progress-circular
                        :indeterminate="true"
                        size="24"
                      ></v-progress-circular>
                    </v-col>
                    <v-col style="padding-left: 5px;">
                      Downloading...
                    </v-col>
                  </v-row>

                  <m-submit v-else label="Download Readings" :model="model">
                  </m-submit>
                </v-col>
              </v-row>
            </m-form>
          </v-card-text>
          <v-card-text>
            <h3 class="darken-1 font-weight-medium">
              Available Downloadable Readings
            </h3>
            <p
              v-if="deviceDownloadableYears.length == 0"
              style="padding: 10px;"
            >
              There are no downloadable readings at this moment
            </p>
            <v-row
              v-for="year of deviceDownloadableYears"
              :key="year"
              align="center"
            >
              <v-col cols="auto">
                <v-btn
                  color="secondary darken-1 white--text"
                  style="padding: 5px 50px"
                  :disabled="deviceRecordsDownloading.isDownloading"
                  @click="downloadDeviceReadings(year)"
                >
                  {{ year }}
                  <v-icon right style="padding-left: 25px;"
                    >mdi-download</v-icon
                  >
                </v-btn>
              </v-col>
              <v-col
                v-if="
                  year == deviceRecordsDownloading.year &&
                    deviceRecordsDownloading.isDownloading
                "
                cols="auto"
              >
                <v-progress-circular
                  :indeterminate="true"
                  size="24"
                ></v-progress-circular>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-container>
    </v-content>
  </v-app>
</template>

<script>
import Vue from "vue";
import apiEndpoints from "../mixins/api-endpoints";
import apiRequest from "../mixins/api-request";
import downloadDataUrl from "../mixins/download-data-url";
import { click } from "ol/events/condition";

export default {
  mixins: [apiRequest, apiEndpoints],
  data() {
    return {
      device: {},
      deviceDetailsFormattedList: [],
      trendReadings: this.$route.query.type == "trend",
      filterData: {
        start_date: "",
        end_date: "",
        all_readings: this.$route.query.type == "trend" ? undefined : true
      },
      deviceDownloadableYears: [],
      availableYears: {
        currentYear: "",
        previousYear: ""
      },
      deviceRecordsDownloading: {
        year: "",
        isDownloading: false
      }
    };
  },
  mounted() {
    this.loadDeviceInfo();
  },
  computed: {},
  methods: {
    showError(message) {
      this.$refs.errorBox.showError(message);
    },
    formatAddress(device) {
      let address = device.address;
      if (device.city && device.state && device.postcode) {
        address += `, ${device.city}, ${device.state} ${device.postcode}`;
      }
      return address ? address : "N/A";
    },
    loadDeviceInfo() {
      const currentYear = new Date().getFullYear();
      const previousYear = new Date(
        new Date().setFullYear(currentYear - 1, 0, 1)
      );

      this.availableYears = {
        currentYear: new Date().toISOString().substring(0, 10),
        previousYear: previousYear.toISOString().substring(0, 10)
      };
      this.apiRequest(this.apiEndpoints.device, [
        this.$route.params.deviceId
      ]).then(r => {
        if (this.statusOk(r.status)) {
          Vue.set(this, "device", r.body.data);
          this.deviceDetailsFormattedList = [
            {
              title: "Online",
              value: `Device is ${this.device.online ? "Online" : "Offline"}`
            },
            { title: "Group", value: this.device.group_name },
            { title: "Device Token", value: this.device.device_token },
            {
              title: "Address",
              value: this.formatAddress(this.device)
            },
            {
              title: "Last Seen",
              value: new Date(this.device.last_contact * 1000).toLocaleString()
            },
            //{ title: "First Reading", value: this.device.first_reading },
            //{ title: "Last Reading", value: this.device.last_reading },
            //{ title: "Latitude", value: this.device.latitude },
            //{ title: "Longitude", value: this.device.longitude },
            { title: "Comments", value: this.device.comments }
          ];
        }
      });

      this.apiRequest(
        this.apiEndpoints.showDeviceDownloadableRecords,
        [this.$route.params.deviceId],
        this.$route.query
      ).then(r => {
        if (this.statusOk(r.status)) {
          this.deviceDownloadableYears = r.body.data;
        }
      });
    },
    downloadReadingsSuccess(result) {
      if (this.statusOk(result.status)) {
        downloadDataUrl.methods.downloadFileFromContentDisposition(result);
      }
    },
    downloadDeviceReadings(year) {
      this.deviceRecordsDownloading = {
        year,
        isDownloading: true
      };
      this.apiRequest(
        this.apiEndpoints.downloadDeviceReadingsByYear,
        [this.$route.params.deviceId],
        {
          ...this.$route.query,
          year
        }
      ).then(r => {
        if (this.statusOk(r.status)) {
          downloadDataUrl.methods.downloadFileFromContentDisposition(r);
        }
        this.deviceRecordsDownloading = {
          year: "",
          isDownloading: false
        };
      });
    }
  }
};
</script>
